import PulseLoading from "@/design-system/pulse-loading";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts/UserAuthenticationContext";
import type { IImageLoader } from "@Components/ImageLoader";
import { type PropsTelemetryCustom, requestTrack } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import * as stylex from "@stylexjs/stylex";

import { CashbackFlame } from "@Assets/CashbackFlame";

import type {
    PropsWithStyleX,
    QueryLgMaxToLgMin,
    QueryMdMaxToMdMin,
    QuerySmMax,
    QueryXsMax,
} from "../../styles/tokens.stylex";
import { colors, genericStyles } from "../../styles/tokens.stylex";
import type { ISpecialCashback } from "../cashback/CashbackCommonInterfaces";
import { RoundLink } from "./RoundLink";
import FavoriteLink from "./links/FavoriteLink";

export interface IRoundTile extends PropsWithStyleX, PropsTelemetryCustom {
    id: string;
    image: IImageLoader;
    href?: string;
    telemetryAction?: string;
    description?: string;
    subDescription?: string;
    secondSubDescription?: string;
    showSave?: boolean;
    specialCashback?: ISpecialCashback;
    favoriteLinkStyleX?: stylex.StyleXStyles;
    hideFlameIcon?: boolean;
}

export default function RoundTile({
    id,
    image,
    href,
    telemetryAction,
    description: brandName,
    subDescription,
    secondSubDescription,
    showSave,
    specialCashback,
    telemetryCustom,
    styleX,
    hideFlameIcon,
    favoriteLinkStyleX,
}: IRoundTile) {
    const resources = languageSelector(
        {
            cbAriaLabel: "{0} zwrotu cashback {2}",
            cbSpecialAriaLabel: "{0} {1}, poprzednio {2} zwrotu cashback",
            shoppingMallAriaLabel: "Sprawdź wszystkie oferty dla centrum handlowego {0}",
            flyerAriaLabel: "Zobacz wszystkie {0} promocje i ulotki",
            logoAlt: "Sklep",
        },
        {
            cbAriaLabel: "{0} cashback {2}",
            cbSpecialAriaLabel: "{0} cashback {1}, previously {2}",
            shoppingMallAriaLabel: "Check all offers for {0} mall",
            flyerAriaLabel: "See all {0} promotions and flyers",
            logoAlt: "Store",
        },
    );

    const renderRoundTile = () => {
        let ariaLabel = "";
        let linkDescriber = "";
        if (href?.indexOf("/centrum-handlowe/") >= 0) {
            linkDescriber = resources.shoppingMallAriaLabel.replace("{0}", brandName || "");
        } else if (href?.indexOf("/gazetka-promocyjna") >= 0) {
            linkDescriber = resources.flyerAriaLabel.replace("{0}", brandName?.replaceAll("<br>", "") || "");
        } else {
            ariaLabel = (specialCashback ? resources.cbSpecialAriaLabel : resources.cbAriaLabel)
                .replace("{0}", brandName || "")
                .replace("{1}", specialCashback?.maxCashback || "")
                .replace("{2}", subDescription || "");

            if (image?.alt) {
                linkDescriber = `${resources.logoAlt} ${image.alt}`;
            }
        }

        const itemId = `list-${id}-details`;

        if (image) {
            return (
                <>
                    <RoundLink
                        specialCashback={!!specialCashback}
                        href={href}
                        onClick={() => {
                            requestTrack(telemetryAction);
                            sendTelemetryEvent(telemetryCustom);
                        }}
                        image={{
                            ...image,
                            srcSetAxis: "width",
                            size: 300,
                            alt: linkDescriber,
                            lazyLoading: true,
                        }}
                        aria-label={linkDescriber}
                    />
                    <div {...stylex.props(styles.details)} role="group" aria-label={ariaLabel} id={itemId}>
                        {brandName && (
                            <Text
                                textElement="h5"
                                styleX={styles.description}
                                fontWeight="bold"
                                dangerouslySetInnerHTML={{ __html: brandName }}
                                aria-labelledby={itemId}
                            />
                        )}
                        {subDescription && specialCashback ? (
                            <div {...stylex.props(styles.cashbackHotDeal)} aria-labelledby={itemId}>
                                <span {...stylex.props(styles.newCashback)}>
                                    {specialCashback.maxCashback}
                                </span>
                                {specialCashback.overrideSegmentMaxCashback ? (
                                    <span {...stylex.props(styles.previousCashback)}>{subDescription}</span>
                                ) : null}
                            </div>
                        ) : (
                            subDescription && (
                                <span {...stylex.props(styles.subDescription)} aria-labelledby={itemId}>
                                    {subDescription}
                                </span>
                            )
                        )}
                        {secondSubDescription && (
                            <span
                                {...stylex.props(styles.secondSubDescription)}
                                dangerouslySetInnerHTML={{ __html: secondSubDescription }}
                                aria-labelledby={itemId}
                            />
                        )}
                    </div>
                    {showSave && (
                        <FavoriteLink
                            className={stylex.props(styles.favoriteLink, favoriteLinkStyleX).className}
                            id={id}
                            favoriteLinkIconSize="border"
                            type="brands"
                            showText={false}
                        />
                    )}
                    {specialCashback && !hideFlameIcon && (
                        <span {...stylex.props(styles.hotFlame)} aria-hidden>
                            <CashbackFlame />
                        </span>
                    )}
                </>
            );
        }
        return (
            <>
                <PulseLoading stylexItem={styles.logoLoading} />
                <PulseLoading
                    stylexItem={styles.descriptionLoading}
                    styleX={styles.descriptionLoadingContainer}
                    pulseNumber={2}
                />
            </>
        );
    };

    return <div {...stylex.props(styles.wrapper, styleX)}>{renderRoundTile()}</div>;
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const mdMax: QueryMdMaxToMdMin = "@media (max-width: 1239px) and (min-width: 990px)";
const smMax: QuerySmMax = "@media (max-width: 989px)";
const lgMax: QueryLgMaxToLgMin = "@media (max-width: 1599px) and (min-width: 1240px)";

const styles = stylex.create({
    wrapper: {
        position: "relative",
        zIndex: 1,
    },

    details: {
        overflow: "hidden",
        textAlign: "center",
        marginTop: "15px",
    },

    subDescription: {
        color: colors.fg_Primary,
        fontSize: {
            default: "1.125rem",
            [xsMax]: "1rem",
        },
        fontWeight: "bold",
        marginTop: "5px",
    },
    secondSubDescription: {
        color: "#505050",
        display: "block",
        fontSize: { default: "0.75rem", [xsMax]: "0.625rem" },
        lineHeight: { default: "0.875rem", [xsMax]: "0.75rem" },
    },

    cashbackHotDeal: {
        lineHeight: "22px",
    },
    newCashback: {
        color: colors.b_BrandMagenta,
        fontSize: "1.125rem",
        fontWeight: "bold",
    },

    previousCashback: {
        textDecoration: "line-through",
        color: colors.grayLight,
        fontSize: "0.875rem",
        marginLeft: "7px",
    },

    favoriteLink: {
        position: "absolute",
        right: {
            default: "-5px",
            [xsMax]: "-8px",
        },
        top: 0,
    },
    description: {
        color: "#505050",
        display: "block",
        fontSize: { default: "0.75rem", [xsMax]: "0.625rem" },
        lineHeight: 1.2,

        textTransform: "uppercase",
        marginBottom: "5px",
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: "0",
    },

    hotFlame: {
        padding: "2px",
        borderRadius: genericStyles.borderRadiusFullRound,
        alignItems: "center",
        backgroundColor: colors.b_BrandMagenta,
        color: colors.fg_AltDefault,
        display: "inline-flex",
        justifyContent: "center",
        position: "absolute",
        height: { default: 20, [lgMax]: 18, [mdMax]: 20, [smMax]: 17 },
        left: 0,
        top: 0,
        width: { default: 20, [lgMax]: 18, [mdMax]: 20, [smMax]: 17 },
    },
    descriptionLoading: {
        margin: 0,
        borderRadius: 20,
        height: {
            default: 20,
            [xsMax]: 15,
        },
        width: { default: "75%", ":last-child": "65%" },
    },
    descriptionLoadingContainer: {
        alignItems: "center",
    },
    logoLoading: {
        borderRadius: genericStyles.borderRadiusFullRound,
        aspectRatio: "1/1",
        boxShadow: "inset 0 0 10px rgba(0, 0, 0, 0.1)",
        marginBottom: "10px",
    },
});
